import React from 'react';

export type FlightEditorFeature =
    | 'schedulingSteps'
    | 'weatherDelay'
    | 'notesForPilot'
    | 'delete'
    | 'printout'
    | 'notify'
    | 'makeExpected'
    | 'lock'
    | 'bless';

export type FlightEditorFormField =
    | 'scheduledFlightDate'
    | 'estDepartureTime'
    | 'desig'
    | 'lastKnownController'
    | 'departureID'
    | 'aircraftID'
    | 'contractID'
    | 'pilotID'
    | 'copilotID'
    | 'weatherDelay';

export type FlightEditorEntityColumn =
    | 'scheduledGroup'
    | 'name'
    | 'employer'
    | 'lastKnownController'
    | 'weight'
    | 'bagWeight'
    | 'bagCount'
    | 'departure'
    | 'destination'
    | 'transitType'
    | 'pilotNotes'
    | 'safetyCard'
    | 'requestedCheckInTime'
    | 'notifiedCheckIn'
    | 'createdBy';

export type UnauthorizedBehavior = 'hide' | 'disable' | 'readOnly';

export interface PermissionRule {
    anyOf?: string[];
    allOf?: string[];
    unauthorized?: UnauthorizedBehavior;
}

export interface FlightEditorFeatureConfiguration {
    enabled: boolean;
    permission?: PermissionRule;
}

export interface FlightEditorActionContext {
    flight: any;
    loading: boolean;
    refetch(): Promise<any>;
    orgData: any;
}

export interface FlightEditorAssignmentContext extends FlightEditorActionContext {
    visible: boolean;
    assignmentMode: 'auto' | 'manual';
    selectedLegs: number[];
    onClose(): void;
    onAssign(paxnodes: any[], cgonodes: any[]): Promise<void>;
}

export interface FlightEditorConfiguration {
    editPermission?: PermissionRule;
    canEditFlight?(flight: any): boolean;
    features: Partial<Record<FlightEditorFeature, FlightEditorFeatureConfiguration>>;
    formFields?: FlightEditorFormField[];
    entityColumns?: FlightEditorEntityColumn[];
    routes(flight: any, loading: boolean): Array<{
        path?: string | null;
        title?: React.ReactNode;
        breadcrumbName?: React.ReactNode;
    }>;
    HeaderActions?: React.ComponentType<FlightEditorActionContext>;
    PrintoutAction?: React.ComponentType<FlightEditorActionContext>;
    AssignmentDrawer?: React.ComponentType<FlightEditorAssignmentContext>;
    layoutClassName?: string;
    layoutStyle?(requestedStyle?: React.CSSProperties): React.CSSProperties | undefined;
}

export interface UnifiedFlightEditorProps {
    flightId?: string;
    orgData?: any;
    configuration: FlightEditorConfiguration;
    style?: React.CSSProperties;
    onBack?: (event?: React.MouseEvent<HTMLElement>) => void;
    onIDChange?(newID: string, previousID?: string): void;
}

export function isPermissionGranted(rule: PermissionRule | undefined, userGroups: string[]): boolean {
    if (!rule) return true;
    const groups = new Set(userGroups || []);
    const anyGranted = !rule.anyOf?.length || rule.anyOf.some(group => groups.has(group));
    const allGranted = !rule.allOf?.length || rule.allOf.every(group => groups.has(group));
    return anyGranted && allGranted;
}

export function isFeatureEnabled(
    configuration: FlightEditorConfiguration,
    feature: FlightEditorFeature,
    userGroups: string[]
): boolean {
    const featureConfiguration = configuration.features[feature];
    if (!featureConfiguration?.enabled) return false;
    if (featureConfiguration.permission?.unauthorized === 'hide') {
        return isPermissionGranted(featureConfiguration.permission, userGroups);
    }
    return true;
}

export function isFeatureAuthorized(
    configuration: FlightEditorConfiguration,
    feature: FlightEditorFeature,
    userGroups: string[]
): boolean {
    return isPermissionGranted(configuration.features[feature]?.permission, userGroups);
}

export function createFlightEditorWrapper<P extends object>(
    UnifiedEditor: React.ComponentType<P & UnifiedFlightEditorProps>,
    buildConfiguration: (props: P) => FlightEditorConfiguration
): React.FC<P & Omit<UnifiedFlightEditorProps, 'configuration'>> {
    const FlightEditorWrapper: React.FC<P & Omit<UnifiedFlightEditorProps, 'configuration'>> = props => (
        <UnifiedEditor {...props as P & UnifiedFlightEditorProps} configuration={buildConfiguration(props)} />
    );
    FlightEditorWrapper.displayName = 'FlightEditorWrapper';
    return FlightEditorWrapper;
}
